import useActivityView from '@/data/hook/useActivityView';
import { updateDNDPositionalActivityProgress } from '@/data/services/activityElement/dndPositionalActivityProgress';
import {
  dndPositionalActivitiesProgressesQueryKeys,
  dndPositionalActivitiesQueryKeys,
} from '@/data/services/querykeys';
import { ActivityTypeEnum } from '@/models/_deprecated/ActivityElement';
import { DNDPositionalCorrectAnswer } from '@/models/_deprecated/ActivityProgress';
import LoadingView from '@/pages/courses/LoadingView';
import alert from '@/utils/shared/alert';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ElementPreviewFactoryProps } from '../ElementPreviewFactory';
import DNDPositionalColumns from './DNDPositionalColumns/DNDPositionalColumns';
import DNDPositionalColumnsProgress from './DNDPositionalColumnsProgress/DNDPositionalColumnsProgress';

export interface DNDPositionalElementProps extends ElementPreviewFactoryProps {
  answer?: DNDPositionalCorrectAnswer | null;
}

export default function DNDPositionalElement(props: DNDPositionalElementProps) {
  const {
    activityMode,
    answer,
    isAdmin,
    questionId,
    progressId,
    attemptElement,
  } = props;
  const { invalidate, status } = useActivityView();

  const { t } = useTranslation('translation', {
    keyPrefix: 'lesson.activity',
  });

  const { queryKey: dndPositionalProgressQueryKey, queryFn: progressQueryFn } =
    dndPositionalActivitiesProgressesQueryKeys.get({ progressId, questionId });

  const handleAnswer = async (hasChange: boolean) => {
    try {
      const data = await updateDNDPositionalActivityProgress(
        {
          activityId: questionId,
          activityProgressId: progressId,
        },
        { isDone: hasChange },
      );

      if (data) {
        await invalidate(ActivityTypeEnum.DND_POSITIONAL, data);
      }
    } catch {
      alert.error(t('answerError'));
    }
  };

  const {
    data: dndPositionalActivity,
    isInitialLoading: isLoadingDndPositionalActivity,
  } = useQuery({
    enabled: !!isAdmin,
    ...dndPositionalActivitiesQueryKeys.get(questionId),
  });

  const {
    data: dndPositionalActivityProgress,
    isInitialLoading: isLoadingDndPositionalActivityProgress,
  } = useQuery({
    enabled:
      !isAdmin && !!questionId && !!progressId && status === 'inProgress',
    queryKey: dndPositionalProgressQueryKey,
    queryFn: progressQueryFn,
  });

  if (
    isLoadingDndPositionalActivity ||
    isLoadingDndPositionalActivityProgress
  ) {
    return <LoadingView />;
  }

  if (dndPositionalActivity) {
    return (
      <DNDPositionalColumns dndPositionalActivity={dndPositionalActivity} />
    );
  }

  if (dndPositionalActivityProgress) {
    return (
      <DNDPositionalColumnsProgress
        dndPositionalActivityProgress={dndPositionalActivityProgress}
        activityMode={activityMode}
        setHasChange={handleAnswer}
        attemptElement={attemptElement}
        answerColumn={answer?.answerColumn}
      />
    );
  }

  return null;
}
